import { Box, Table } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'

import { observer } from 'mobx-react'

import useEmployeeMultiFilters from 'modules/core/components/EmployeeMultiFilters/hooks/useEmployeeMultiFilters'
import employeeStatuses from 'modules/core/components/EmployeeMultiFilters/hooks/utils/employeeStatuses'
import createComputed from 'modules/core/hooks/createComputed'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import type FiltersForm from 'modules/core/lib/forms/Filters'
import i18n from 'modules/core/lib/i18n'

import SearchBar from 'modules/payroll_bundle/components/common/components/_SearchBar'
import getUserByItem from 'modules/payroll_bundle/components/common/helper/getUserByItem'
import { ERROR, SUCCESS } from 'modules/payroll_bundle/lib/constants'
import type PaymentItem from 'modules/payroll_bundle/store/models/PaymentItem'

import { disablePaymentItem } from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/_common/disableRowItem'
import filterValues from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/_common/filterValues'
import { useSearchFilter } from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/useSearchFilter'
import Header from './Header'
import PaymentGroups from './PaymentGroups'
import { GroupProp } from './types'

interface Props {
  checkable: boolean
  onSelectAll: (filteredPayments: PaymentItem[]) => void
  selected: Set<number>
  selectable: Array<number | null>
  onSelectItem: (paymentItem: PaymentItem) => void
  paymentItems: PaymentItem[]
  filtersForm: FiltersForm
}

const FILTER_KEY = LOCAL_STORAGE_KEYS.payrollPaymentsFilters

const useGroupPaymentItems = createComputed((itemsToShow: PaymentItem[]) => {
  const errorRows = i18n.t('payroll_bundle.results_payments.payments.generate_payment.error_rows')
  const successRows = i18n.t(
    'payroll_bundle.results_payments.payments.generate_payment.success_rows'
  )

  const defaultGroupResultConceptItems: GroupProp[] = [
    {
      name: errorRows,
      count: 0,
      status: ERROR,
      paymentItem: [],
    },
    {
      name: successRows,
      count: 0,
      status: SUCCESS,
      paymentItem: [],
    },
  ]

  return itemsToShow.reduce((acc: GroupProp[], paymentItem: PaymentItem) => {
    const user = getUserByItem(paymentItem)
    const paymentDisabled = disablePaymentItem(paymentItem, user)
    const paymentStatus = paymentDisabled ? ERROR : SUCCESS
    const data = acc.find((item) => item.status === paymentStatus)

    // FIXME: This is mutating a value inside acc and also not checking undefined values.
    data!.count++
    data?.paymentItem.push(paymentItem)

    return acc
  }, defaultGroupResultConceptItems)
})

const PaymentsTable = ({
  checkable,
  onSelectAll,
  selected,
  selectable,
  onSelectItem,
  paymentItems,
  filtersForm,
}: Props) => {
  const DEFAULT_FILTER_VALUES = [
    {
      field: 'employee_status',
      value: employeeStatuses.active,
    },
    {
      field: 'employee_status',
      value: employeeStatuses.terminated,
    },
  ]

  const EMPLOYEE_FILTER_OPTIONS = {
    skipDefaultFilters: DEFAULT_FILTER_VALUES,
  }
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const {
    filteredModels: filteredPayrollPaymentItems,
    logicOperator,
    filters,
    onApplyFilters,
    onCancel,
    onRemoveFilter,
    onAddFilter,
  } = useEmployeeMultiFilters<PaymentItem>(filterValues, FILTER_KEY, EMPLOYEE_FILTER_OPTIONS)

  const itemsToShow = useSearchFilter(debouncedSearch, filteredPayrollPaymentItems).get()
  const groupPaymentItemsToShow = useGroupPaymentItems(itemsToShow).get()

  useUnsafeEffect(() => {
    onApplyFilters(paymentItems)
  }, [paymentItems]) // eslint-disable-line react-hooks/exhaustive-deps

  const Options = () => (
    <Box
      width="full"
      alignItems="center"
      flexDirection="row"
      background="grey200"
      justifyContent="spaceBetween"
    >
      <SearchBar
        search={search}
        onSearch={onSearch}
        onCancel={onCancel}
        filters={filters}
        onAddFilter={onAddFilter}
        logicOperator={logicOperator}
        onRemoveFilter={onRemoveFilter}
        onApplyFilters={() => onApplyFilters(paymentItems)}
      />
    </Box>
  )

  return (
    <Table autoLayout fullWidth options={Options}>
      <Table noRadius hideBorder>
        <Header
          checkable={checkable}
          selected={selected}
          selectable={selectable}
          filtersForm={filtersForm}
          onSelectAll={() => onSelectAll(itemsToShow)}
        />
      </Table>
      <Table noRadius hideBorder>
        <PaymentGroups
          groups={groupPaymentItemsToShow}
          checkable={checkable}
          onSelectItem={onSelectItem}
          selected={selected}
        />
      </Table>
    </Table>
  )
}

export default observer(PaymentsTable)
